import React, { useEffect, useRef, useState } from 'react';
import {
  adminService,
  OnlineService,
  OnlineServicePayload,
  ServicePlanPayload,
  SERVICE_CATEGORIES,
} from './adminService';
import { ProductError } from '../product/productService';
import { Button } from '@/components/ui/button';

// Платформы актуальны в основном для игровых сервисов (PS Plus, Xbox, Steam)
const PLATFORM_OPTIONS = ['PS4', 'PS5', 'Xbox One', 'Xbox Series X/S', 'Steam', 'Nintendo Switch'];

const rubleFormatter = new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'RUB',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const categoryLabel = (value: string) =>
  SERVICE_CATEGORIES.find((c) => c.value === value)?.label ?? value;

interface PlanFormState {
  id?: number; // id существующего тарифа (для diff при обновлении); отсутствует у новых
  tier: string;
  durationMonths: string;
  price: string;
  description: string;
  picture: string;
}

interface ServiceFormState {
  name: string;
  description: string;
  price: string;
  picture: string;
  category: string;
  platforms: string[];
  screenshots: string[];
  brandNew: boolean;
  salesLeader: boolean;
  plans: PlanFormState[];
}

const emptyPlan: PlanFormState = { tier: '', durationMonths: '', price: '', description: '', picture: '' };
const SUBSCRIPTION_TIERS = ['Essential', 'Extra', 'Deluxe'] as const;

const initialFormState: ServiceFormState = {
  name: '',
  description: '',
  price: '',
  picture: '',
  category: 'GameService',
  platforms: [],
  screenshots: [],
  brandNew: false,
  salesLeader: false,
  plans: [],
};

const mapServiceToFormState = (s: OnlineService): ServiceFormState => ({
  name: s.name ?? '',
  description: s.description ?? '',
  price: s.price ? String(s.price) : '',
  picture: s.picture ?? '',
  category: s.category ?? 'GameService',
  platforms: s.platforms ?? [],
  screenshots: s.screenshots ?? [],
  brandNew: Boolean(s.brandNew),
  salesLeader: Boolean(s.salesLeader),
  plans: (s.plans ?? []).map((p) => ({
    id: p.id,
    tier: p.tier ?? '',
    durationMonths: p.durationMonths != null ? String(p.durationMonths) : '',
    price: p.price ? String(p.price) : '',
    description: p.description ?? '',
    picture: p.picture ?? '',
  })),
});

// Автоназвание тарифа, если админ не задал tier явно (Name у Product обязателен)
const planName = (serviceName: string, plan: PlanFormState): string => {
  const parts = [plan.tier?.trim() || serviceName.trim() || 'Тариф'];
  if (plan.durationMonths.trim()) parts.push(`${plan.durationMonths.trim()} мес`);
  return parts.join(' · ');
};

const buildPayload = (
  form: ServiceFormState
): { payload: OnlineServicePayload; error: string | null } => {
  const name = form.name.trim();
  const price = form.price.trim() === '' ? 0 : Number(form.price.trim().replace(',', '.'));

  const base: OnlineServicePayload = {
    name,
    description: form.description.trim(),
    picture: form.picture.trim(),
    price,
    category: form.category,
    platforms: form.platforms,
    screenshots: form.screenshots,
    brandNew: form.brandNew,
    salesLeader: form.salesLeader,
    plans: [],
  };

  if (!name) return { payload: base, error: 'Заполни название сервиса.' };
  if (Number.isNaN(price) || price < 0)
    return { payload: base, error: 'Цена должна быть числом не меньше 0.' };

  const plans: ServicePlanPayload[] = [];
  for (const p of form.plans) {
    const planPrice = p.price.trim() === '' ? NaN : Number(p.price.trim().replace(',', '.'));
    if (Number.isNaN(planPrice) || planPrice < 0)
      return { payload: base, error: 'У каждого тарифа должна быть корректная цена.' };
    const duration = p.durationMonths.trim() === '' ? null : Number(p.durationMonths.trim());
    if (duration != null && (Number.isNaN(duration) || duration <= 0))
      return { payload: base, error: 'Длительность тарифа — целое число месяцев больше 0.' };
    plans.push({
      id: p.id,
      name: planName(name, p),
      description: p.description.trim(),
      price: planPrice,
      tier: p.tier.trim() || null,
      durationMonths: duration,
    });
  }

  return { payload: { ...base, plans }, error: null };
};

interface ServiceFormModalProps {
  mode: 'create' | 'edit';
  form: ServiceFormState;
  error: string | null;
  saving: boolean;
  uploading: boolean;
  uploadError: string | null;
  onClose: () => void;
  onChange: (patch: Partial<ServiceFormState>) => void;
  onMasterFileSelected: (file: File) => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  fixedCategory?: string;
  tierUploading: string | null;
  onTierFileSelected: (tier: string, file: File) => void;
}

const ServiceFormModal = ({
  mode,
  form,
  error,
  saving,
  uploading,
  uploadError,
  onClose,
  onChange,
  onMasterFileSelected,
  onSubmit,
  fixedCategory,
  tierUploading,
  onTierFileSelected,
}: ServiceFormModalProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onMasterFileSelected(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const updatePlan = (index: number, patch: Partial<PlanFormState>) => {
    onChange({
      plans: form.plans.map((p, i) => (i === index ? { ...p, ...patch } : p)),
    });
  };

  const addPlan = () => onChange({ plans: [...form.plans, { ...emptyPlan }] });
  const removePlan = (index: number) =>
    onChange({ plans: form.plans.filter((_, i) => i !== index) });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-gray-900/50 px-4 py-6">
      <div className="my-auto w-full max-w-2xl rounded-3xl bg-white p-6 shadow-2xl sm:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {mode === 'create' ? 'Новый онлайн-сервис' : 'Редактирование сервиса'}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Игровые сервисы, развлечения, AI и другие подписки.
            </p>
          </div>

          <Button
            type="button"
            onClick={onClose}
            variant="outline"
            size="icon"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 text-gray-500 transition hover:bg-gray-100"
            aria-label="Закрыть форму"
          >
            ×
          </Button>
        </div>

        <form className="mt-6 space-y-5" onSubmit={onSubmit}>
          <div className="grid gap-5 sm:grid-cols-2">
            <label className="block">
              <span className="mb-2 block text-sm font-medium text-gray-700">Название</span>
              <input
                type="text"
                value={form.name}
                onChange={(e) => onChange({ name: e.target.value })}
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-gray-400 focus:bg-white"
                placeholder="Например, PlayStation Plus"
              />
            </label>

            <label className="block">
              <span className="mb-2 block text-sm font-medium text-gray-700">Категория</span>
              <select
                value={form.category}
                onChange={(e) => onChange({ category: e.target.value })}
                disabled={Boolean(fixedCategory)}
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-gray-400 focus:bg-white"
              >
                {SERVICE_CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="mb-2 block text-sm font-medium text-gray-700">
                Базовая цена
              </span>
              <input
                type="text"
                inputMode="decimal"
                value={form.price}
                onChange={(e) => onChange({ price: e.target.value })}
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-gray-400 focus:bg-white"
                placeholder="Например, 599"
              />
            </label>

            <div className="block">
              <span className="mb-2 block text-sm font-medium text-gray-700">Изображение</span>
              <div
                onClick={() => fileInputRef.current?.click()}
                className="relative mb-2 flex h-28 cursor-pointer items-center justify-center overflow-hidden rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 transition hover:border-gray-400 hover:bg-gray-100"
              >
                {form.picture ? (
                  <img src={form.picture} alt="preview" className="h-full w-full object-cover" />
                ) : (
                  <span className="text-xs text-gray-400">
                    {uploading ? 'Загрузка...' : 'Нажмите для загрузки'}
                  </span>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
              {uploadError && <p className="mb-2 text-xs text-red-600">{uploadError}</p>}
              <input
                type="url"
                value={form.picture}
                onChange={(e) => onChange({ picture: e.target.value })}
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 outline-none transition focus:border-gray-400 focus:bg-white"
                placeholder="или вставьте URL изображения"
              />
            </div>
          </div>

          <div className="block">
            <span className="mb-2 block text-sm font-medium text-gray-700">
              Платформы <span className="text-gray-400">(для игровых сервисов)</span>
            </span>
            <div className="flex flex-wrap gap-2">
              {PLATFORM_OPTIONS.map((p) => (
                <label
                  key={p}
                  className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 rounded border-gray-300"
                    checked={form.platforms.includes(p)}
                    onChange={(e) =>
                      onChange({
                        platforms: e.target.checked
                          ? [...form.platforms, p]
                          : form.platforms.filter((x) => x !== p),
                      })
                    }
                  />
                  {p}
                </label>
              ))}
            </div>
          </div>

          <label className="block">
            <span className="mb-2 block text-sm font-medium text-gray-700">Описание</span>
            <textarea
              value={form.description}
              onChange={(e) => onChange({ description: e.target.value })}
              rows={3}
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-900 outline-none transition focus:border-gray-400 focus:bg-white"
              placeholder="Кратко опиши сервис (необязательно)"
            />
          </label>

          {/* Тарифы */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Тарифы / длительности</span>
              <button
                type="button"
                onClick={addPlan}
                className="text-sm font-medium text-gray-600 transition hover:text-gray-900"
              >
                + Добавить тариф
              </button>
            </div>

            {form.plans.length === 0 ? (
              <p className="rounded-xl border border-dashed border-gray-200 px-4 py-3 text-sm text-gray-400">
                Тарифы не добавлены. Можно продать сервис по базовой цене выше.
              </p>
            ) : (
              <div className="space-y-2">
                {form.plans.map((plan, i) => (
                  <div
                    key={i}
                    className="rounded-xl border border-gray-200 bg-gray-50 p-3"
                  >
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto_auto_auto]">
                    <input
                      type="text"
                      value={plan.tier}
                      onChange={(e) => updatePlan(i, { tier: e.target.value })}
                      className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-gray-400 disabled:opacity-60"
                      placeholder="Уровень (Essential / Premium)"
                    />
                    <input
                      type="text"
                      inputMode="numeric"
                      value={plan.durationMonths}
                      onChange={(e) => updatePlan(i, { durationMonths: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-gray-400 disabled:opacity-60 sm:w-28"
                      placeholder="Мес."
                    />
                    <input
                      type="text"
                      inputMode="decimal"
                      value={plan.price}
                      onChange={(e) => updatePlan(i, { price: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-gray-400 disabled:opacity-60 sm:w-28"
                      placeholder="Цена"
                    />
                    <button
                      type="button"
                      onClick={() => removePlan(i)}
                      className="rounded-lg border border-red-200 bg-white px-3 py-2 text-sm text-red-600 transition hover:bg-red-50"
                    >
                      Удалить
                    </button>
                    </div>
                    <textarea
                      value={plan.description}
                      onChange={(e) => updatePlan(i, { description: e.target.value })}
                      rows={3}
                      className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-gray-400"
                      placeholder="Преимущества тарифа — каждое с новой строки"
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {fixedCategory === 'Subscription' && (
            <div>
              <span className="mb-2 block text-sm font-medium text-gray-700">Изображения уровней</span>
              <p className="mb-3 text-xs text-gray-500">Одна картинка используется для тарифов уровня на 1, 3 и 12 месяцев.</p>
              <div className="grid gap-3 sm:grid-cols-3">
                {SUBSCRIPTION_TIERS.map((tier) => {
                  const tierPlan = form.plans.find(plan => plan.tier.trim().toLowerCase() === tier.toLowerCase());
                  return (
                    <label key={tier} className={`overflow-hidden rounded-xl border border-gray-200 bg-gray-50 ${tierPlan ? 'cursor-pointer hover:border-gray-400' : 'cursor-not-allowed opacity-50'}`}>
                      <div className="flex aspect-[21/10] items-center justify-center overflow-hidden bg-gray-100">
                        {tierPlan?.picture ? <img src={tierPlan.picture} alt={tier} className="h-full w-full object-cover" /> : <span className="px-2 text-center text-xs text-gray-400">{tierUploading === tier ? 'Загрузка...' : 'Добавьте тариф уровня'}</span>}
                      </div>
                      <div className="px-3 py-2 text-center text-sm font-medium text-gray-700">{tier}</div>
                      <input type="file" accept="image/*" className="hidden" disabled={!tierPlan} onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (file) onTierFileSelected(tier, file);
                        event.currentTarget.value = '';
                      }} />
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
              <input
                type="checkbox"
                checked={form.brandNew}
                onChange={(e) => onChange({ brandNew: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Новинка</span>
            </label>
            <label className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
              <input
                type="checkbox"
                checked={form.salesLeader}
                onChange={(e) => onChange({ salesLeader: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Лидер продаж</span>
            </label>
          </div>

          {error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-100"
            >
              Отмена
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {saving ? 'Сохранение...' : mode === 'create' ? 'Создать сервис' : 'Сохранить'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const PAGE_SIZE = 20;

interface AdminServicesPageProps {
  fixedCategory?: string;
  title?: string;
  subtitle?: string;
}

const AdminServicesPage = ({
  fixedCategory,
  title = 'Онлайн-сервисы',
  subtitle,
}: AdminServicesPageProps) => {
  const [services, setServices] = useState<OnlineService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState(fixedCategory ?? '');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [modalMode, setModalMode] = useState<'create' | 'edit' | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<ServiceFormState>(initialFormState);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Картинку при создании держим файлом и грузим к id после создания сервиса
  const [masterFile, setMasterFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [tierFiles, setTierFiles] = useState<Record<string, File>>({});
  const [tierUploading, setTierUploading] = useState<string | null>(null);

  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const load = async (pageNum: number, query: string, cat: string) => {
    try {
      setLoading(true);
      setError(null);
      const res = await adminService.fetchOnlineServices({
        name: query.trim() || undefined,
        category: cat || undefined,
        pageNumber: pageNum,
        pageSize: PAGE_SIZE,
      });
      setServices(res.products);
      setTotal(res.total);
    } catch (err) {
      setError((err as ProductError).message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(page, search, category);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, category]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      load(1, value, category);
    }, 350);
  };

  const patchForm = (patch: Partial<ServiceFormState>) =>
    setForm((prev) => ({ ...prev, ...patch }));

  const openCreate = () => {
    setForm({ ...initialFormState, category: fixedCategory ?? initialFormState.category });
    setSaveError(null);
    setUploadError(null);
    setMasterFile(null);
    setTierFiles({});
    setEditingId(null);
    setModalMode('create');
  };

  const openEdit = async (id: number) => {
    setSaveError(null);
    setUploadError(null);
    setMasterFile(null);
    setTierFiles({});
    setEditingId(id);
    setModalMode('edit');
    try {
      const service = await adminService.fetchOnlineService(id);
      setForm(mapServiceToFormState(service));
    } catch {
      setSaveError('Не удалось загрузить сервис для редактирования.');
    }
  };

  const closeModal = () => {
    if (saving || uploading) return;
    setModalMode(null);
    setEditingId(null);
    setForm(initialFormState);
    setSaveError(null);
    setUploadError(null);
    setMasterFile(null);
    setTierFiles({});
  };

  // При создании — стейджим файл и показываем локальное превью; при редактировании
  // товар уже существует, поэтому грузим картинку сразу к его id.
  const handleMasterFileSelected = async (file: File) => {
    if (modalMode === 'edit' && editingId != null) {
      try {
        setUploading(true);
        setUploadError(null);
        const url = await adminService.uploadMasterImage(editingId, file);
        patchForm({ picture: url });
      } catch {
        setUploadError('Не удалось загрузить изображение');
      } finally {
        setUploading(false);
      }
    } else {
      setMasterFile(file);
      patchForm({ picture: URL.createObjectURL(file) });
    }
  };

  const handleTierFileSelected = (tier: string, file: File) => {
    setTierFiles(prev => ({ ...prev, [tier]: file }));
    const preview = URL.createObjectURL(file);
    setForm(prev => ({
      ...prev,
      plans: prev.plans.map(plan =>
        plan.tier.trim().toLowerCase() === tier.toLowerCase() ? { ...plan, picture: preview } : plan
      ),
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { payload, error: payloadError } = buildPayload(form);
    if (payloadError) {
      setSaveError(payloadError);
      return;
    }

    try {
      setSaving(true);
      setSaveError(null);
      let saved: OnlineService;
      if (modalMode === 'edit' && editingId != null) {
        saved = await adminService.updateOnlineService(editingId, payload);
      } else {
        saved = await adminService.createOnlineService(payload);
        // Картинку грузим уже к созданному сервису
        if (masterFile && saved?.id) {
          await adminService.uploadMasterImage(saved.id, masterFile);
        }
      }
      for (const [tier, file] of Object.entries(tierFiles)) {
        const representative = (saved.plans ?? []).find(plan => plan.tier?.trim().toLowerCase() === tier.toLowerCase());
        if (representative?.id) {
          setTierUploading(tier);
          await adminService.uploadMasterImage(representative.id, file);
        }
      }
      setTierUploading(null);
      setSaving(false);
      closeModal();
      load(page, search, category);
    } catch (err) {
      setSaveError((err as ProductError).message || 'Не удалось сохранить сервис.');
    } finally {
      setSaving(false);
      setTierUploading(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeletingId(deleteTarget.id);
      setDeleteError(null);
      await adminService.deleteOnlineService(deleteTarget.id);
      setServices((prev) => prev.filter((s) => s.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      setDeleteError((err as ProductError).message || 'Не удалось удалить сервис.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6 bg-white">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">{title}</h1>
          <p className="mt-1 text-sm text-gray-500">{subtitle ? `${subtitle} · ` : ''}Всего: {total}</p>
        </div>
        <Button
          onClick={openCreate}
          className="inline-flex w-full items-center justify-center rounded-xl bg-gray-900 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-gray-800 sm:w-auto"
        >
          + {fixedCategory === 'Subscription' ? 'Добавить подписку' : 'Добавить сервис'}
        </Button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-4 border-b border-gray-100 p-5 md:flex-row md:items-center md:justify-between">
          {!fixedCategory && <div className="w-full md:w-56">
            <select
              value={category}
              onChange={(e) => {
                setPage(1);
                setCategory(e.target.value);
              }}
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 outline-none transition focus:border-gray-400 focus:bg-white"
            >
              <option value="">Все категории</option>
              {SERVICE_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>}
          <div className="w-full md:w-80">
            <input
              type="text"
              placeholder="Поиск по названию..."
              value={search}
              onChange={handleSearchChange}
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-gray-400 focus:bg-white"
            />
          </div>
        </div>

        {loading ? (
          <div className="grid gap-4 p-5 sm:grid-cols-2 xl:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-2xl border border-gray-200 p-5">
                <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
                <div className="mt-4 h-8 w-20 animate-pulse rounded bg-gray-100" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="p-6">
            <div className="rounded-2xl border border-red-200 bg-red-50 p-6">
              <h2 className="text-lg font-semibold text-red-700">Не удалось загрузить сервисы</h2>
              <p className="mt-2 text-sm text-red-600">{error}</p>
              <Button
                onClick={() => load(page, search, category)}
                variant="destructive"
                className="mt-4 inline-flex items-center rounded-xl px-4 py-2 text-sm font-medium"
              >
                Попробовать снова
              </Button>
            </div>
          </div>
        ) : services.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-14 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gray-100 text-2xl">
              🎮
            </div>
            <h3 className="mt-4 text-lg font-semibold text-gray-900">Сервисов пока нет</h3>
            <p className="mt-2 max-w-md text-sm text-gray-500">
              Добавь первый онлайн-сервис — игровой сервис или медиа-подписку.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr className="border-b border-gray-200">
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Название
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Категория
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Цена
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {services.map((s) => (
                  <tr key={s.id} className="transition hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <p className="text-sm font-semibold text-gray-900">{s.name}</p>
                      <p className="text-xs text-gray-500">#{s.id}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                        {categoryLabel(s.category)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                      {rubleFormatter.format(s.price)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <Button
                          onClick={() => openEdit(s.id)}
                          variant="outline"
                          className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 transition hover:bg-gray-100"
                        >
                          Редактировать
                        </Button>
                        <Button
                          onClick={() => {
                            setDeleteError(null);
                            setDeleteTarget({ id: s.id, name: s.name });
                          }}
                          disabled={deletingId === s.id}
                          variant="outline"
                          className="rounded-lg border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {deletingId === s.id ? 'Удаление...' : 'Удалить'}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-100 px-5 py-4">
                <p className="text-sm text-gray-500">
                  {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} из {total}
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    onClick={() => setPage((p) => p - 1)}
                    disabled={page === 1}
                    variant="outline"
                    className="h-9 w-9 rounded-lg border border-gray-200 p-0 text-gray-600 disabled:opacity-40"
                  >
                    ‹
                  </Button>
                  <span className="px-2 text-sm text-gray-600">
                    {page} / {totalPages}
                  </span>
                  <Button
                    type="button"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page === totalPages}
                    variant="outline"
                    className="h-9 w-9 rounded-lg border border-gray-200 p-0 text-gray-600 disabled:opacity-40"
                  >
                    ›
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {modalMode && (
        <ServiceFormModal
          mode={modalMode}
          form={form}
          error={saveError}
          saving={saving}
          uploading={uploading}
          uploadError={uploadError}
          onClose={closeModal}
          onChange={patchForm}
          onMasterFileSelected={handleMasterFileSelected}
          onSubmit={handleSubmit}
          fixedCategory={fixedCategory}
          tierUploading={tierUploading}
          onTierFileSelected={handleTierFileSelected}
        />
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 px-4 py-6">
          <div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl sm:p-8">
            <h2 className="text-xl font-bold text-gray-900">Удалить сервис?</h2>
            <p className="mt-2 text-sm text-gray-500">
              Сервис <span className="font-semibold text-gray-900">{deleteTarget.name}</span> будет
              скрыт из каталога.
            </p>
            {deleteError && (
              <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {deleteError}
              </div>
            )}
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button
                type="button"
                onClick={() => {
                  if (deletingId !== deleteTarget.id) {
                    setDeleteTarget(null);
                    setDeleteError(null);
                  }
                }}
                disabled={deletingId === deleteTarget.id}
                variant="outline"
                className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Отмена
              </Button>
              <Button
                type="button"
                onClick={handleDelete}
                disabled={deletingId === deleteTarget.id}
                variant="destructive"
                className="inline-flex items-center justify-center rounded-xl px-4 py-2.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deletingId === deleteTarget.id ? 'Удаление...' : 'Подтвердить удаление'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminServicesPage;
